import { Stack, useGlobalSearchParams } from 'expo-router';
import { ApolloProvider } from '@apollo/client';
import { useEffect, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { ToastProvider } from 'react-native-toast-notifications';
import PatientAuthService from '@/services/PatientAuthService';
import { PatientFormProvider } from '@/contexts/PatientFormContext';
import { Color } from '@/GlobalStyles';
import BaseContainer from '@/components/BaseContainer';

export default function PatientLayout() {
  const { token } = useGlobalSearchParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentToken, setCurrentToken] = useState<string | null>(null);

  // Initialiser le client Apollo au démarrage
  useEffect(() => {
    const initializeClient = async () => {
      try {
        // Attendre un peu pour laisser le temps à [token].tsx de sauvegarder le token
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Utiliser getTokenRaw() pour éviter la validation qui pourrait nettoyer le token temporaire
        const tokenData = await PatientAuthService.getTokenRaw();
        const newToken = (token as string) || tokenData?.token || null;

        console.log(
          '[PatientLayout] Initialisation du client Apollo avec token:',
          newToken ? newToken.substring(0, 8) + '...' : 'null'
        );

        if (newToken) {
          const apolloClient = PatientAuthService.getApolloClient(newToken, true); // forceNew = true
          setClient(apolloClient);
          setCurrentToken(newToken);
        } else {
          // Pas de token, créer un client sans authentification
          console.log('[PatientLayout] Pas de token trouvé, client Apollo sans auth');
          const apolloClient = PatientAuthService.getApolloClient(null, true);
          setClient(apolloClient);
          setCurrentToken(null);
        }
      } catch (error) {
        console.error("Erreur lors de l'initialisation du client patient:", error);
        // Créer un client Apollo sans authentification en cas d'erreur
        const apolloClient = PatientAuthService.getApolloClient(null, true);
        setClient(apolloClient);
        setCurrentToken(null);
      } finally {
        setLoading(false);
      }
    };

    initializeClient();
  }, []);

  // Écouter les changements du token pour recharger le client si nécessaire
  useEffect(() => {
    if (!loading) {
      const checkTokenChange = async () => {
        try {
          // Utiliser getTokenRaw() pour éviter la validation
          const tokenData = await PatientAuthService.getTokenRaw();
          const newToken = tokenData?.token || null;

          // Si le token a changé, recréer le client Apollo
          if (newToken && newToken !== currentToken) {
            console.log('[PatientLayout] Token changé détecté, recréation du client Apollo');
            const apolloClient = PatientAuthService.getApolloClient(newToken, true);
            setClient(apolloClient);
            setCurrentToken(newToken);
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du token:', error);
        }
      };

      // Vérifier le token toutes les 30 secondes
      const interval = setInterval(checkTokenChange, 30000);

      return () => clearInterval(interval);
    }
  }, [loading, currentToken]);

  if (loading) {
    return (
      <BaseContainer centered>
        <ActivityIndicator size="large" color="#678aae" />
      </BaseContainer>
    );
  }

  if (!client) {
    return null; // Ne devrait jamais arriver maintenant
  }

  return (
    <ApolloProvider client={client}>
      <ToastProvider>
        <PatientFormProvider>
          <SafeAreaProvider>
            <Stack
              screenOptions={{
                headerShown: false,
                headerStyle: {
                  backgroundColor: Color.MainBleu,
                },
                headerTintColor: '#fff',
                headerTitleStyle: {
                  fontFamily: 'Roboto_Medium',
                  fontSize: 18,
                },
                animation: 'slide_from_right',
                headerBackTitleVisible: false,
              }}
            />
          </SafeAreaProvider>
        </PatientFormProvider>
      </ToastProvider>
    </ApolloProvider>
  );
}
